// Libraries
import React, { useState } from "react";
import { useObserver } from "mobx-react-lite";
import { Cell, Grid, useRouter } from "@shoothill/core";
import { InputAdornment, Link, TextField } from "@material-ui/core";
import { Link as RouterLink } from "react-router-dom";

// Styling & Images
import { DetailsHeader } from "Globals/Views/DetailsPage/DetailsHeader";
import { DetailsPage } from "Globals/Views/DetailsPage/DetailsPage.styles";
import { VariationFutureSpendAmendmentViewModel } from "./VariationFutureSpendAmendmentViewModel";
import { VariationFutureSpendAmendmentDTO, VariationFutureSpendAmendmentModel } from "./VariationFutureSpendAmendmentModel";
import { AppUrls } from "AppUrls";
import { formatCurrencyFromPounds, formatDateTime } from "Utils/Format";
import MoneySVG from "Content/Money.svg";
import { DarwinMoneyInput } from "Globals/Styles/Control/DarwinInput";
import {
    FutureSpendAmendmentAddButton,
    FutureSpendItem,
    FutureSpendItemContentRow,
    FutureSpendItemDetailsRow,
    FutureSpendItemNoteRow,
    FutureSpendPORefRow,
    NewFutureSpendContainer,
    NewFutureSpendLabel,
} from "../FutureSpendAmendment/IEFutureSpendAmendment.styles";
import { BackButtonHolder } from "../IETables/IEGrid.Styles";
import { CustomArrow } from "Views/Project/CustomComponents";
import { theme } from "Globals/Styles/AppTheme";
import { IEGridItemViewModel } from "../IEmodels/IEGridItemViewModel";

export const VariationFutureSpendAmendmentView: React.FunctionComponent = () => {
    const { match } = useRouter();
    const { ieid, variationid, variationitemid } = match.params as any;
    const [viewModel] = useState(() => new VariationFutureSpendAmendmentViewModel(ieid, variationid, variationitemid));
    const { history } = useRouter();

    const COLUMNS8 = "1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr";
    const COLUMNS12 = "1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr";

    const renderProjectImpactFormatted = (projectImpact: number) => {
        let className = "value-orange";
        let icon = <>&#8594;</>;

        if (projectImpact > 0) {
            className = "value-green";
            icon = <>&#8593;</>;
        } else if (projectImpact < 0) {
            className = "value-red";
            icon = <>&#8595;</>;
        }

        return (
            <>
                <div>
                    Project impact:{" "}
                    <span className={className}>
                        {icon} {formatCurrencyFromPounds(projectImpact)}
                    </span>
                </div>
            </>
        );
    };

    const renderFutureSpendHistoryAmendment = (item: VariationFutureSpendAmendmentDTO) => {
        return (
            <FutureSpendItem>
                <FutureSpendItemContentRow>
                    <div>{item.futureSpendType}</div>
                    <div></div>
                    <div className="future-spend-group">
                        <div>Previous: {formatCurrencyFromPounds(item.previousFutureSpend)}</div>
                        <div>New: {formatCurrencyFromPounds(item.futureSpend)}</div>
                        {renderProjectImpactFormatted(item.projectImpact)}
                    </div>
                </FutureSpendItemContentRow>
                <FutureSpendItemDetailsRow>
                    <div>
                        <div>
                            <strong>Created by:</strong>
                        </div>
                        <div>{item.amendmentCreatedByUserName}</div>
                    </div>
                    <div>
                        <div>
                            <strong>Created date:</strong>
                        </div>
                        <div>{formatDateTime(item.createdDate)}</div>
                    </div>
                </FutureSpendItemDetailsRow>
                <FutureSpendItemNoteRow>
                    <div>
                        <strong>Notes:</strong>
                    </div>
                    <div className={"keep-newline-formatting"}>{item.note}</div>
                </FutureSpendItemNoteRow>
            </FutureSpendItem>
        );
    };

    const renderFutureSpendHistoryPO = (item: VariationFutureSpendAmendmentDTO) => {
        return (
            <FutureSpendItem>
                <FutureSpendItemContentRow>
                    <div>
                        <img src={MoneySVG} /> {item.futureSpendType}
                    </div>
                    <div>Order value: {formatCurrencyFromPounds(item.orderValue)}</div>
                    <div className="future-spend-group">
                        <div>Previous: {formatCurrencyFromPounds(item.previousFutureSpend)}</div>
                        <div>New: {formatCurrencyFromPounds(item.futureSpend)}</div>
                        {renderProjectImpactFormatted(item.projectImpact)}
                    </div>
                </FutureSpendItemContentRow>
                <FutureSpendPORefRow>
                    Ref:{" "}
                    <Link component={RouterLink} to={AppUrls.Client.PurchaseOrder.Edit.replace(":poid", item.poId).replace(":approvalMode", "false")}>
                        {item.formattedPONumberWithRevision}
                    </Link>
                </FutureSpendPORefRow>
                <FutureSpendItemDetailsRow>
                    <div>
                        <div>
                            <strong>Created by:</strong>
                        </div>
                        <div>{item.amendmentCreatedByUserName}</div>
                    </div>
                    <div>
                        <div>
                            <strong>Created date:</strong>
                        </div>
                        <div>{formatDateTime(item.createdDate)}</div>
                    </div>
                </FutureSpendItemDetailsRow>
                <FutureSpendItemNoteRow>
                    <div>
                        <strong>Notes:</strong>
                    </div>
                    <div>{item.note}</div>
                </FutureSpendItemNoteRow>
            </FutureSpendItem>
        );
    };

    const renderFutureSpendHistoryVariationItem = (item: VariationFutureSpendAmendmentDTO) => {
        return (
            <FutureSpendItem>
                <FutureSpendItemContentRow>
                    <div>{item.futureSpendType}</div>
                    <div>Order value: {formatCurrencyFromPounds(item.orderValue)}</div>
                    <div className="future-spend-group">
                        <div>Previous: £0.00</div>
                        <div>New: {formatCurrencyFromPounds(item.futureSpend)}</div>
                        {renderProjectImpactFormatted(item.projectImpact)}
                    </div>
                </FutureSpendItemContentRow>
                <FutureSpendItemDetailsRow>
                    <div>
                        <div>
                            <strong>Created by:</strong>
                        </div>
                        <div>{item.amendmentCreatedByUserName}</div>
                    </div>
                    <div>
                        <div>
                            <strong>Created date:</strong>
                        </div>
                        <div>{formatDateTime(item.createdDate)}</div>
                    </div>
                </FutureSpendItemDetailsRow>
            </FutureSpendItem>
        );
    };

    const renderFutureSpendHistory = () => {
        return viewModel.variationFutureSpendAmendmentItems.map((item, index) => {
            if (item.isPO) {
                return renderFutureSpendHistoryPO(item);
            } else if (item.isAmendment) {
                return renderFutureSpendHistoryAmendment(item);
            } else if (!item.isPO && !item.isAmendment) {
                return renderFutureSpendHistoryVariationItem(item);
            }
        });
    };

    return useObserver(() => (
        <DetailsPage>
            <BackButtonHolder
                onClick={() => {
                    if (IEGridItemViewModel.Instance.isCentral) {
                        history.push(AppUrls.Client.Central.View.replace(":ieid", ieid ? ieid : viewModel.model.ieId));
                    } else {
                        history.push(AppUrls.Client.Project.IE.replace(":ieid", ieid ? ieid : viewModel.model.ieId));
                    }
                }}
                style={{ minHeight: "30px", paddingTop: "3px" }}
            >
                <div style={{ position: "relative", height: "30px", display: "flex", marginLeft: "10px" }}>
                    <div style={{ position: "absolute", top: "5px" }}>
                        <CustomArrow size={"8px"} color={theme.palette.blue.main} type={"left"} />
                    </div>
                    <div style={{ marginLeft: "20px", fontSize: "18px", fontWeight: "bold", color: theme.palette.blue.main }}>Back to {viewModel.getIETitle}</div>
                </div>
            </BackButtonHolder>
            <DetailsHeader viewModel={viewModel.getHeader} />
            <div style={{ margin: "0 30px 17.5px 30px" }}>
                <NewFutureSpendContainer>
                    <Grid columnGap={30} rowGap={20} tc={COLUMNS8} dc={COLUMNS8}>
                        {/* Group of 8 columns */}
                        <Cell ts={1} ds={1}>
                            <NewFutureSpendLabel>New future spend</NewFutureSpendLabel>
                        </Cell>
                        <Cell ts={1} ds={1}>
                            <DarwinMoneyInput<VariationFutureSpendAmendmentModel>
                                type="number"
                                label=""
                                placeholder=""
                                validateOnBlur={true}
                                viewModel={viewModel}
                                fieldName="futureSpend"
                                shrink={true}
                                maxLength={32}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start">£</InputAdornment>,
                                }}
                                editMode={viewModel.canAddFutureSpendAmendment}
                            />
                        </Cell>
                    </Grid>

                    <TextField
                        id="amendment_notes"
                        multiline
                        rows={6}
                        value={viewModel.model.note}
                        onChange={(event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => viewModel.setNote(event.target.value)}
                        fullWidth
                        className="textarea"
                        label="Notes"
                        placeholder="Notes"
                        disabled={!viewModel.canAddFutureSpendAmendment}
                    />

                    <FutureSpendAmendmentAddButton displayName="Add" execute={viewModel.handleSave} fullWidth={true} canExecute={viewModel.canAddFutureSpendAmendment} />
                </NewFutureSpendContainer>

                {renderFutureSpendHistory()}

                {/* <Grid columnGap={30} rowGap={20} tc={"1fr 1fr 1fr 1fr"} dc={"1fr 1fr 1fr 1fr"}>
                    {viewModel!.server.HaveValidationMessage && (
                        <Cell tabletSpan={4} desktopSpan={4}>
                            <Alert severity="error">
                                <AlertTitle>Error</AlertTitle>
                                {viewModel!.server.ValidationMessage}
                            </Alert>
                        </Cell>
                    )}
                </Grid> */}
            </div>
        </DetailsPage>
    ));
};
